import React from "react";
import { PiFireSimpleFill } from "react-icons/pi";
import ContinueButton from "../components/Buttons/ContinueButton";
import { getPlatform } from "@/lib/iap";
import FireAnimation from "../components/PlacementTest/FireAnimation";

interface StreakProps {
  onContinue: () => void;
  streak: number;
}

// Helper to get day names in order, starting from the first day of the streak
const getDayNames = (streak: number) => {
  const days = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const today = new Date().getDay(); // 0 is Sunday, 1 is Monday, etc.
  
  // Calculate the first day of the streak (streak - 1 days before today)
  const firstDayOfStreak = (today - (streak - 1) + 7) % 7;
  
  // Return days in chronological order starting from the first day of the streak
  return days.map((_, i) => {
    const dayIndex = (firstDayOfStreak + i) % 7;
    return days[dayIndex];
  });
};

const StreakPage: React.FC<StreakProps> = ({ onContinue, streak }) => {
  const days = getDayNames(streak);
  const platform = getPlatform();

  // Helper to determine if a day should be colored based on streak
  const shouldColorDay = (index: number) => {
    // If streak is 0, only color today
    if (streak === 0) return index === 0;
    
    // Color the first 'streak' number of days
    return index < streak;
  };

  return (
    <div className="appPadding py-0 flex flex-col h-full w-full overflow-hidden items-center">
      {/* Top spacer */}
      <div className="flex-grow mt-16" />
      
      {/* Fire animation section */}
      <div className="my-2 text-center flex flex-col items-center w-full font-bold text-[var(--color-orange)]">
        <div className="w-[170px] h-[170px]">
          <FireAnimation />
        </div>
        <div className="text-7xl font-['Fuzzy_Bubbles'] font-[700] leading-[1.1]">
          {streak}
        </div>
        <div className="text-3xl">
          day streak
        </div>
      </div>

      {/* Middle spacer */}
      <div className="flex-grow" />

      {/* White box section */}
      <div className="bg-white rounded-xl shadow-sm p-5 pt-4 pb-2 border border-[var(--color-grey-lightest)] mx-4 max-w-[500px]">
        <div className="flex justify-between items-center mb-2 pb-2 border-b border-[var(--color-grey-lightest)]">
          {days.map((d, i) => (
            <div key={d} className="flex flex-col items-center">
              <span
                className="relative inline-block"
                style={{ width: 32, height: 32 }}
              >
                <PiFireSimpleFill
                  color={shouldColorDay(i) ? "var(--color-orange)" : "#E0E0E0"}
                  size={32}
                />
              </span>
              <span
                className={`text-sm font-semibold ${
                  shouldColorDay(i)
                    ? "text-[var(--color-orange)]"
                    : "text-[#BDBDBD]"
                }`}
              >
                {d}
              </span>
            </div>
          ))}
        </div>
        <div className="text-[var(--color-grey)] text-center mt-1">
          Practice each day so your streak won't reset!
        </div>
      </div>

      {/* Bottom spacer */}
      <div className="flex-grow" />

      {/* Continue button section */}
      <div
        className={`flex justify-center items-center w-full ${
          platform === "web" ? "bottom-padding-web" : "pb-6"
        }`}
      >
        <ContinueButton
          onClick={onContinue}
          text="I'm committed"
          disabled={false}
        />
      </div>
    </div>
  );
};

export default StreakPage;
